/**
 * Loading Skeleton Component
 * Reusable skeleton placeholders matching MetricCard, chart, and table layouts
 */

import React from 'react';
import { Box, Card, CardContent, Skeleton, Grid, Container } from '@mui/material';

function MetricSkeleton() {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton width={80} height={16} sx={{ mb: 1 }} />
            <Skeleton width={120} height={32} />
          </Box>
          <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: '8px' }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton width={60} height={24} sx={{ borderRadius: 2 }} />
          <Skeleton width={100} height={16} />
        </Box>
      </CardContent>
    </Card>
  );
}

function ChartSkeleton({ height = 300 }) {
  return (
    <Card>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Skeleton width={160} height={22} sx={{ mb: 0.5 }} />
            <Skeleton width={220} height={16} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton width={60} height={32} sx={{ borderRadius: 2 }} />
            <Skeleton width={60} height={32} sx={{ borderRadius: 2 }} />
          </Box>
        </Box>
        <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 2 }} />
      </CardContent>
    </Card>
  );
}

function TableSkeleton({ rows = 5 }) {
  return (
    <Card>
      <CardContent sx={{ p: 2.5 }}>
        <Skeleton width={180} height={22} sx={{ mb: 2 }} />
        {/* Header row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          {[120, 100, 80, 100, 60].map((w, i) => (
            <Skeleton key={i} width={w} height={16} />
          ))}
        </Box>
        {/* Data rows */}
        {Array.from({ length: rows }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            {[120, 100, 80, 100, 60].map((w, j) => (
              <Skeleton key={j} width={w} height={14} />
            ))}
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}

function PageSkeleton() {
  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Skeleton width={40} height={3} sx={{ mb: 1.5, borderRadius: 2 }} />
        <Skeleton width={200} height={32} sx={{ mb: 0.5 }} />
        <Skeleton width={300} height={16} />
      </Box>

      {/* Metric cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[0, 1, 2, 3].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <MetricSkeleton />
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <ChartSkeleton />
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartSkeleton height={200} />
        </Grid>
      </Grid>

      {/* Table */}
      <TableSkeleton />
    </Box>
  );
}

function LoadingSkeleton({ variant = 'page', rows, height, contained = false }) {
  let body;
  switch (variant) {
    case 'metric':
      body = <MetricSkeleton />;
      break;
    case 'chart':
      body = <ChartSkeleton height={height} />;
      break;
    case 'table':
      body = <TableSkeleton rows={rows} />;
      break;
    case 'page':
    default:
      // `contained` matches the loaded content: centered in the page Container
      // with the page background, so the skeleton doesn't span edge-to-edge or
      // jump in width when real content arrives.
      body = contained ? (
        <Box sx={{ bgcolor: 'background.default', py: { xs: 5, md: 7 } }}>
          <Container maxWidth="lg">
            <PageSkeleton />
          </Container>
        </Box>
      ) : (
        <PageSkeleton />
      );
  }
  // Announced to assistive tech as a busy region while content loads.
  return (
    <Box role="status" aria-busy="true" aria-label="Loading">
      {body}
    </Box>
  );
}

export default LoadingSkeleton;
